/**
 * SlashActionMenu — popup со spec-командами для любого чата (не бот-DM).
 * Команды: «Файл с диска», «Файл из редактора», «Поставить задачу»,
 * «Примечание к сделке».
 *
 * Стиль — наш tg-style: line-SVG в tinted чипе, без цветных эмодзи.
 *
 * Триггер из родителя: input value начинается с «/» → query=строка после «/».
 * Стрелки ↑↓ / Enter — выбор. Esc / клик вне — закрыть.
 */
import { useEffect, useMemo, useRef } from "react";
import { Icon } from "@/components/prototype/Icon";

export type SlashAction = "drive" | "editor" | "task" | "project_note";

export interface SlashActionItem {
  action: SlashAction;
  icon: string;       // Имя иконки из @/components/prototype/Icon (line-SVG)
  label: string;
  desc: string;
  keywords: string[];  // alternative search keys
}

export const SLASH_ACTIONS: SlashActionItem[] = [
  { action: "drive", icon: "folder", label: "Файл с диска",
    desc: "выбрать файл из любого диска",
    keywords: ["drive", "диск", "файл", "file"] },
  { action: "editor", icon: "edit", label: "Файл из редактора",
    desc: "выбрать редактируемый документ",
    keywords: ["editor", "редактор", "edit", "документ", "docs"] },
  { action: "task",  icon: "check", label: "Поставить задачу",
    desc: "создать задачу для команды",
    keywords: ["task", "задача", "задачник", "tasks"] },
  { action: "project_note", icon: "report", label: "Примечание к сделке",
    desc: "оставить запись в производстве + AmoCRM",
    keywords: ["note", "примечание", "сделка", "проект", "production", "amo", "amocrm"] },
];

export function filterSlashActions(query: string | null): SlashActionItem[] {
  if (query == null) return [];
  const q = query.toLowerCase().trim();
  if (!q.startsWith("/")) return [];
  if (q === "/") return SLASH_ACTIONS;
  const term = q.slice(1);
  return SLASH_ACTIONS.filter(it =>
    it.label.toLowerCase().includes(term) ||
    it.keywords.some(k => k.startsWith(term)),
  );
}

export function SlashActionMenu({
  query, selectedIdx, onPick, onClose,
}: {
  query: string | null;
  selectedIdx: number;
  onPick: (action: SlashAction) => void;
  onClose: () => void;
}) {
  const items = useMemo(() => filterSlashActions(query), [query]);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ block: "nearest" });
  }, [selectedIdx]);

  useEffect(() => {
    if (query == null) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (listRef.current && listRef.current.contains(t)) return;
      if (t.closest(".chat-form-input, .chat-form-bot-menu")) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [query, onClose]);

  if (query == null || items.length === 0) return null;

  return (
    <div className="bot-slash-menu slash-action-menu" ref={listRef} onMouseDown={(e) => e.preventDefault()}>
      {items.map((it, i) => (
        <button
          key={it.action}
          type="button"
          data-idx={i}
          className={`bot-slash-item${i === selectedIdx ? " is-active" : ""}`}
          onClick={() => { onPick(it.action); onClose(); }}
        >
          <span className="slash-action-icon"><Icon name={it.icon} size={16}/></span>
          <span className="bot-slash-cmd">{it.label}</span>
          <span className="bot-slash-desc">— {it.desc}</span>
        </button>
      ))}
    </div>
  );
}
